import React, { useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
} from "firebase/auth";
import { getFirestore, collection, query, where, getDocs, updateDoc, doc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { useUser } from "./UserProvider.tsx";

const LoginPage: React.FC = () => {
  const [noMatrik, setNoMatrik] = useState("");
  const [password, setPassword] = useState("");
  const [isRegistering, setIsRegistering] = useState(false);

  const auth = getAuth();
  const firestore = getFirestore();
  const { setUser } = useUser();
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!noMatrik) {
      toast.error("Please enter your matric number.");
      return;
    }

    try {
      const email = `${noMatrik}@ikas.com`;

      // Attempt to log in
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // Retrieve the user's uid from Firebase Authentication
      const uid = userCredential.user.uid;

      // Store uid in UserProvider
      setUser({ sessionId: uid });

      toast.success("Login successful!");
      setTimeout(() => navigate("/"), 3000); // Redirect after 3 seconds
    } catch (err: any) {
      console.error("Login error:", err.message);
      toast.error("Login failed. Please try again.");
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
  
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters long.");
      return;
    }
  
    try {
      // Convert noMatrik to a number since Firestore stores it as a number
      const matricNumber = Number(noMatrik);
      if (isNaN(matricNumber)) {
        toast.error("Matric number must be a valid number.");
        return;
      }
  
      // Query Firestore collection for a document with a matching `no_matrik`
      const studentsCollection = collection(firestore, "students");
      const q = query(studentsCollection, where("no_matrik", "==", matricNumber));
      const querySnapshot = await getDocs(q);
  
      if (!querySnapshot.empty) {
        console.log("Matched documents:", querySnapshot.docs.map((doc) => doc.data()));
  
        const email = `${matricNumber}@ikas.com`;
  
        // Create user with Firebase Authentication
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  
        // Retrieve the UID of the newly created user
        const uid = userCredential.user.uid;
  
        // Update Firestore document with the UID
        const [studentDoc] = querySnapshot.docs; // Get the first matched document
        const studentDocRef = doc(firestore, "students", studentDoc.id); // Reference to the document
  
        await updateDoc(studentDocRef, {
          uid, // Add or update the UID field in the Firestore document
        });
  
        toast.success("Account created successfully! You can now log in.");
        setIsRegistering(false); // Return to login mode
      } else {
        console.log("No document found with no_matrik:", matricNumber);
        toast.error("No matric number found. Please contact support.");
      }
    } catch (err: any) {
      console.error("Registration error:", err.message);
      toast.error("Registration failed. Please try again.");
    }
  };
  
  
  return (
    <div
      className="relative min-h-screen bg-cover bg-center"
      style={{
        backgroundImage: `url('https://tronova.azmiproductions.com/img/backy.png')`,
      }}
    >
      <Toaster />
      <div className="absolute inset-0 bg-black bg-opacity-60"></div>
      <div className="relative z-10 flex items-center justify-center min-h-screen">
        <div className="bg-gray-800 bg-opacity-90 p-8 rounded-xl shadow-lg max-w-md w-full">
          <div className="flex justify-center mb-6">
            <img
              src="https://tronova.azmiproductions.com/ikas/ikas.png"
              alt="Logo"
              className="w-24 h-24"
            />
          </div>
          <h2 className="text-3xl uppercase font-bold text-white text-center mb-4">
            {isRegistering ? "Register" : "Portal Ikas"}
          </h2>
          <form className="space-y-6" onSubmit={isRegistering ? handleRegister : handleLogin}>
            <div>
              <label htmlFor="matric" className="block text-gray-400 mb-2">
                Matric Number
              </label>
              <input
                type="text"
                id="matric"
                value={noMatrik}
                onChange={(e) => setNoMatrik(e.target.value)}
                placeholder="Enter your matric number"
                className="w-full p-3 rounded-lg bg-gray-700 text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-gray-400 mb-2">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="w-full p-3 rounded-lg bg-gray-700 text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-semibold transition shadow-md hover:shadow-lg"
            >
              {isRegistering ? "Register" : "Login"}
            </button>
          </form>
          {!isRegistering && (
            <p className="text-sm text-gray-400 text-center mt-4">
              Don't have an account?{" "}
              <button
                onClick={() => setIsRegistering(true)}
                className="text-blue-500 hover:underline"
              >
                Register here
              </button>
            </p>
          )}
          {isRegistering && (
            <p className="text-sm text-gray-400 text-center mt-4">
              Already have an account?{" "}
              <button
                onClick={() => setIsRegistering(false)}
                className="text-blue-500 hover:underline"
              >
                Login here
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
